// Utilities that determine result content with caching for on-exit screen transitions

import { useContext, useRef } from 'react'
import { ILayout, MediaWithUrl, TemplateRoute } from 'types'
import { SessionContext, useExplorerConfig } from '../components/providers'
import { LayoutHelpers } from '../desktopTypes'
import { layoutOverrides } from './layoutUtils'
import {
  TraitWithSelections,
  getFilledTraitMap,
  useCurrentBackgrounds,
  useTraitsWithSelections,
} from './traitUtils'

export function useResultLayout() {
  const { templateDocs } = useExplorerConfig()
  const { routeSteps } = useContext(SessionContext)

  const layout = useRef<ILayout & LayoutHelpers>(null)

  const { layoutMap } = templateDocs!.template
  const lastStep = routeSteps[routeSteps.length - 1] as TemplateRoute | undefined
  if (lastStep?.type === 'layout')
    layout.current = {
      ...(layoutOverrides[lastStep.key] || layoutMap?.[lastStep.key]),
      keyPath: lastStep.key.toString(),
    }

  return layout.current
}

export function useResultBackground() {
  const currentBackgrounds = useCurrentBackgrounds()
  const background = useRef<MediaWithUrl>(null)
  const currentBackground = currentBackgrounds[currentBackgrounds.length - 1]
  if (currentBackground) background.current = currentBackground

  return background.current
}

export function useResultTraitGroupsWithSelections(): TraitWithSelections[] {
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue } = useContext(SessionContext)
  const latestTraits = useTraitsWithSelections()
  const traits = useRef<TraitWithSelections[]>([])

  if (latestTraits.length > traits.current.length) traits.current = latestTraits

  // If there are no selections, show all traits as selected for builder preview
  if (!latestTraits.length && !traits.current.length) {
    const traitMap = getFilledTraitMap(templateDocs)

    traits.current = Object.entries(traitMap).map(([key, trait]) => ({
      ...trait,
      selected: trait.values.map(v => ({
        groupTitle: getLanguageValue(trait?.title),
        id: v.id,
        title: getLanguageValue(v.title),
      })),
      step: { key: Number(key), type: 'trait' },
    }))
  }

  return traits.current
}
